import React from 'react';
import PropTypes from 'prop-types';
import tracking from '@g4/g4-analytics';

import GAClickEventTracker from '../../components/GAClickEventTracker';
import * as FlightDetails from './searchResults/FlightDetails';
import {
  useMedia,
  Layout,
  VerticalSpacing,
  Box,
  ConditionalWrap,
} from '../../components/Boneyard';

import HorizontallyAlign from '../../components/HorizontallyAlign';
import { useTranslations } from '../../graphql/components/TranslationsProvider';
import { propTypes as flightSearchCriteriaPropTypes } from '../../hooks/useFlightSearchCriteria';
import FlightChooser from './searchResults/FlightChooser';
import RoundTripDiscountBanner from './searchResults/RoundTripDiscountBanner';
import { CALENDAR_VIEW, DEPARTING, RETURNING } from './constants';
import { withValue } from '@g4/automation-hooks';
import {
  DEPARTING_FLIGHT_DETAILS,
  RETURNING_FLIGHT_DETAILS,
  FLIGHTS_LIST_VIEW,
} from '../../../automationHooks.json';
import SearchResultsViewToggle from './searchResults/SearchResultsViewToggle';
import useViewToggle from './useViewToggle';
import useListView from './useListView';
import useRoundTrip from './useRoundTrip';
import useStadiumGame from '../../hooks/useStadiumGame';
import LowfareCalendar from './searchResults/flightCalendar/LowfareCalendar';

const flightsQueryPropTypes = PropTypes.shape({
  flights: PropTypes.arrayOf(PropTypes.object),
  isFetching: PropTypes.bool,
  lowFareQuery: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.object,
    lowFares: PropTypes.array,
  }),
  originAirport: PropTypes.string,
  destinationAirport: PropTypes.string,
  loading: PropTypes.bool,
});

const propTypes = {
  departingQuery: flightsQueryPropTypes,
  returningQuery: flightsQueryPropTypes,
  selectedFlightOptions: PropTypes.shape({
    [DEPARTING]: PropTypes.object,
    [RETURNING]: PropTypes.object,
  }).isRequired,
  lastAvailableDates: PropTypes.shape({
    [DEPARTING]: PropTypes.string,
    [RETURNING]: PropTypes.string,
  }).isRequired,
  earliestAvailableDates: PropTypes.shape({
    [DEPARTING]: PropTypes.string,
    [RETURNING]: PropTypes.string,
  }).isRequired,
  makeOneway: PropTypes.func.isRequired,
  makeRoundtrip: PropTypes.func.isRequired,
  setDepartingTabs: PropTypes.func.isRequired,
  setReturningTabs: PropTypes.func.isRequired,
  setDepartingFlightOption: PropTypes.func.isRequired,
  setReturningFlightOption: PropTypes.func.isRequired,
  setReturningTabsWithoutClearingWarning: PropTypes.func.isRequired,
  tabDates: PropTypes.shape({
    [DEPARTING]: PropTypes.shape({
      middle: PropTypes.string,
      selected: PropTypes.string,
    }),
    [RETURNING]: PropTypes.shape({
      middle: PropTypes.string,
      selected: PropTypes.string,
    }),
  }),
  invalidState: PropTypes.object,
  flightsWarning: PropTypes.object,
  flightSearchCriteria: flightSearchCriteriaPropTypes,
  isOnewayWithRoundTripDiscount: PropTypes.bool,
  onewayToRoundtripSavedAmount: PropTypes.number,
  roundtripDiscountValue: PropTypes.number,
  displayReturningSelectedDate: PropTypes.bool,
  setDisplayReturningSelectedDate: PropTypes.func,
  shouldReturnFlightOptionBeNull: PropTypes.bool.isRequired,
  setShouldReturnFlightOptionBeNull: PropTypes.func.isRequired,
  warning: PropTypes.object,
  deactivateWarning: PropTypes.func,
  setIsArrowClicked: PropTypes.func,
  loyaltyPointsValue: PropTypes.number,
  backupTabDate: PropTypes.object,
  setBackupTabDate: PropTypes.func,
  isRedemptionEnabledForNavitaire: PropTypes.bool,
  googleAnalyticsData: PropTypes.object,
  selectedBundles: PropTypes.object,
  onBundleSelect: PropTypes.func,
  bundleOffers: PropTypes.array,
  allAncillariesList: PropTypes.array,
};

const SearchResults = React.memo(
  ({
    flightSearchCriteria,
    invalidState,
    flightsWarning,
    makeOneway,
    makeRoundtrip,
    tabDates,
    isOnewayWithRoundTripDiscount,
    onewayToRoundtripSavedAmount,
    roundtripDiscountValue,
    departingQuery,
    returningQuery,
    setDepartingTabs,
    setReturningTabs,
    setReturningTabsWithoutClearingWarning,
    selectedFlightOptions,
    setDepartingFlightOption,
    setReturningFlightOption,
    earliestAvailableDates,
    lastAvailableDates,
    displayReturningSelectedDate,
    setDisplayReturningSelectedDate,
    shouldReturnFlightOptionBeNull,
    setShouldReturnFlightOptionBeNull,
    warning,
    deactivateWarning,
    setIsArrowClicked,
    loyaltyPointsValue,
    backupTabDate,
    setBackupTabDate,
    isRedemptionEnabledForNavitaire,
    googleAnalyticsData,
    selectedBundles,
    onBundleSelect,
    bundleOffers,
    allAncillariesList,
  }) => {
    const translations = useTranslations();
    const mq = useMedia();
    const [stadiumGame] = useStadiumGame();

    const { view, handleViewToggle } = useViewToggle({
      tabDates,
      setDepartingTabs,
      setReturningTabs,
      backupTabDate,
      setBackupTabDate,
    });

    const {
      isRoundTrip,
      isCreatingRoundTrip,
      lateRoundTrip,
      scrollToRef,
      queueMakingRoundtrip,
      queueCancelingRoundtrip,
    } = useRoundTrip(
      flightSearchCriteria.tripType,
      tabDates,
      returningQuery,
      departingQuery,
      makeRoundtrip,
      makeOneway,
      setReturningFlightOption,
      handleViewToggle,
      view,
      displayReturningSelectedDate,
      setDisplayReturningSelectedDate
    );

    const {
      shouldAutoSelect,
      setShouldAutoSelect,
      handleArrowClick,
    } = useListView(
      tabDates,
      setDepartingTabs,
      setReturningTabs,
      setDepartingFlightOption,
      setReturningFlightOption,
      setIsArrowClicked,
      selectedFlightOptions
    );

    const {
      departingFlightHeading,
      returningFlightHeading,
      departingFlightOriginLabel,
      departingFlightDestinationLabel,
      returningFlightOriginLabel,
      returningFlightDestinationLabel,
    } = translations.flightsSearchResults;

    const { adultsCount, childrenCount, infantCount } = flightSearchCriteria;
    const numberOfPassengers = adultsCount + childrenCount + infantCount;

    return (
      <>
        <>
          <FlightDetails.Wrapper
            reduceVerticalSpacing={view[DEPARTING] === 'CALENDAR_VIEW'}
          >
            <div style={{ display: 'flex', flexGrow: 1, flexWrap: 'wrap' }}>
              <div
                style={{
                  display: 'flex',
                  flexGrow: 1,
                  flexDirection: 'column',
                }}
              >
                <FlightDetails.Title
                  title={departingFlightHeading}
                  automationHook={DEPARTING_FLIGHT_DETAILS.hook}
                />
                <VerticalSpacing />
                <FlightDetails.Info
                  airportLabel={departingFlightOriginLabel}
                  airport={departingQuery.originAirport}
                  automationHook={DEPARTING_FLIGHT_DETAILS.hook}
                  namespace="origin"
                />
                <VerticalSpacing size={1} />
                <FlightDetails.Info
                  airportLabel={departingFlightDestinationLabel}
                  airport={departingQuery.destinationAirport}
                  automationHook={DEPARTING_FLIGHT_DETAILS.hook}
                  namespace="destination"
                />
              </div>
              {mq.lt('md') ? (
                <div style={{ width: '100%', marginTop: '20px' }} />
              ) : null}
              <SearchResultsViewToggle
                onToggle={handleViewToggle}
                leg={DEPARTING}
                currentView={view[DEPARTING]}
              />
            </div>
          </FlightDetails.Wrapper>
          {view[DEPARTING] === CALENDAR_VIEW ? (
            <LowfareCalendar
              backupTabDate={backupTabDate}
              setBackupTabDate={setBackupTabDate}
              view={view}
              leg={DEPARTING}
              isRoundTrip={isRoundTrip}
              tabDates={tabDates}
              handleViewToggle={handleViewToggle}
              setSelectedDate={setDepartingTabs}
              setSelectedFlight={setDepartingFlightOption}
              lowFareQuery={departingQuery.lowFareQuery}
              lastAvailableDate={lastAvailableDates[DEPARTING]}
              stadiumGame={stadiumGame}
              shouldAutoSelect={shouldAutoSelect[DEPARTING]}
              setShouldAutoSelect={setShouldAutoSelect}
            />
          ) : (
            <FlightChooser
              leg={DEPARTING}
              flights={departingQuery.flights}
              lowFareQuery={departingQuery.lowFareQuery}
              isFetchingMoreFlights={departingQuery.loading}
              flightSearchCriteria={flightSearchCriteria}
              numberOfPassengers={numberOfPassengers}
              selectedFlightOption={selectedFlightOptions[DEPARTING]}
              oppositeLegSelection={selectedFlightOptions[RETURNING]}
              setSelectedFlight={setDepartingFlightOption}
              selectedTabDate={tabDates[DEPARTING].selected}
              oppositeSelectedTabDate={tabDates[RETURNING].selected}
              middleTabDate={tabDates[DEPARTING].middle}
              lastAvailableDate={lastAvailableDates[DEPARTING]}
              earliestAvailableDate={earliestAvailableDates[DEPARTING]}
              invalidState={flightsWarning || invalidState}
              setSelectedTabDate={setDepartingTabs}
              lastAvailableDates={lastAvailableDates}
              automationHook={withValue(FLIGHTS_LIST_VIEW.hook, DEPARTING)}
              shouldAutoSelect={shouldAutoSelect[DEPARTING]}
              setShouldAutoSelect={setShouldAutoSelect}
              onArrowClick={handleArrowClick}
              shouldReturnFlightOptionBeNull={shouldReturnFlightOptionBeNull}
              setShouldReturnFlightOptionBeNull={
                setShouldReturnFlightOptionBeNull
              }
              warning={warning}
              deactivateWarning={deactivateWarning}
              loyaltyPointsValue={loyaltyPointsValue}
              isRedemptionEnabledForNavitaire={isRedemptionEnabledForNavitaire}
              googleAnalyticsData={googleAnalyticsData}
              selectedBundles={selectedBundles}
              onBundleSelect={onBundleSelect}
              bundleOffers={bundleOffers}
              allAncillariesList={allAncillariesList}
            />
          )}
          {!isRoundTrip && !isOnewayWithRoundTripDiscount ? (
            <VerticalSpacing size={mq.lt('md') ? 4 : 6} />
          ) : null}
        </>
        {isOnewayWithRoundTripDiscount ? (
          <>
            <VerticalSpacing size={mq.lt('md') ? 4 : 6} />
            <ConditionalWrap
              condition={mq.lt('md')}
              wrap={children => <Layout.FullWidth children={children} />}
            >
              <GAClickEventTracker
                onClick={() =>
                  tracking.trackEvent(
                    'site_click_tracking',
                    'Flight Chooser',
                    'Round trip discount | Add returning flight'
                  )
                }
              >
                <RoundTripDiscountBanner
                  onClick={queueMakingRoundtrip}
                  amount={onewayToRoundtripSavedAmount * numberOfPassengers}
                />
              </GAClickEventTracker>
            </ConditionalWrap>
            <VerticalSpacing size={mq.lt('md') ? 4 : 6} />
          </>
        ) : null}
        {isRoundTrip && lateRoundTrip ? (
          <>
            <VerticalSpacing size={mq.lt('md') ? 4 : 6} />
            <ConditionalWrap
              condition={mq.lt('md')}
              wrap={children => <Layout.FullWidth children={children} />}
            >
              <GAClickEventTracker
                onClick={() =>
                  tracking.trackEvent(
                    'site_click_tracking',
                    'Flight Chooser',
                    'Round trip discount | Hide return flights'
                  )
                }
              >
                <RoundTripDiscountBanner
                  onClick={queueCancelingRoundtrip}
                  amount={onewayToRoundtripSavedAmount}
                  lateRoundTrip={lateRoundTrip}
                />
              </GAClickEventTracker>
            </ConditionalWrap>
            <VerticalSpacing size={mq.lt('md') ? 4 : 6} />
          </>
        ) : null}
        {isRoundTrip ? (
          <>
            <VerticalSpacing size={mq.lt('md') ? 4 : 6} />
            <FlightDetails.Wrapper
              reduceVerticalSpacing={view[RETURNING] === 'CALENDAR_VIEW'}
            >
              <div style={{ display: 'flex', flexGrow: 1, flexWrap: 'wrap' }}>
                <div
                  style={{
                    display: 'flex',
                    flexGrow: 1,
                    flexDirection: 'column',
                  }}
                >
                  <ConditionalWrap
                    condition={roundtripDiscountValue && mq.gte('md')}
                    wrap={children => (
                      <HorizontallyAlign justifyContent="space-between">
                        <Box>{children}</Box>
                      </HorizontallyAlign>
                    )}
                  >
                    <FlightDetails.Title
                      fwdRef={scrollToRef}
                      title={returningFlightHeading}
                      automationHook={RETURNING_FLIGHT_DETAILS.hook}
                    />
                    <VerticalSpacing />
                    <FlightDetails.Info
                      airportLabel={returningFlightOriginLabel}
                      automationHook={RETURNING_FLIGHT_DETAILS.hook}
                      airport={
                        returningQuery.originAirport ||
                        departingQuery.destinationAirport
                      }
                      namespace="origin"
                    />
                    <VerticalSpacing size={1} />
                    <FlightDetails.Info
                      airportLabel={returningFlightDestinationLabel}
                      automationHook={RETURNING_FLIGHT_DETAILS.hook}
                      airport={
                        returningQuery.destinationAirport ||
                        departingQuery.originAirport
                      }
                      namespace="destination"
                    />
                  </ConditionalWrap>
                </div>
                {mq.lt('md') ? (
                  <div style={{ width: '100%', marginTop: '20px' }} />
                ) : null}
                <SearchResultsViewToggle
                  onToggle={handleViewToggle}
                  leg={RETURNING}
                  currentView={view[RETURNING]}
                />
              </div>
            </FlightDetails.Wrapper>
            {view[RETURNING] === CALENDAR_VIEW ? (
              <LowfareCalendar
                backupTabDate={backupTabDate}
                setBackupTabDate={setBackupTabDate}
                view={view}
                leg={RETURNING}
                isRoundTrip={isRoundTrip}
                tabDates={tabDates}
                handleViewToggle={handleViewToggle}
                setSelectedDate={setReturningTabs}
                setSelectedFlight={setReturningFlightOption}
                lowFareQuery={returningQuery.lowFareQuery}
                lastAvailableDate={lastAvailableDates[RETURNING]}
                stadiumGame={stadiumGame}
                shouldAutoSelect={shouldAutoSelect[RETURNING]}
                setShouldAutoSelect={setShouldAutoSelect}
              />
            ) : (
              <FlightChooser
                leg={RETURNING}
                flights={returningQuery.flights}
                lowFareQuery={returningQuery.lowFareQuery}
                flightSearchCriteria={flightSearchCriteria}
                numberOfPassengers={numberOfPassengers}
                selectedFlightOption={selectedFlightOptions[RETURNING]}
                oppositeLegSelection={selectedFlightOptions[DEPARTING]}
                setSelectedFlight={setReturningFlightOption}
                selectedTabDate={tabDates[RETURNING].selected}
                oppositeSelectedTabDate={tabDates[DEPARTING].selected}
                middleTabDate={tabDates[RETURNING].middle}
                lastAvailableDate={lastAvailableDates[RETURNING]}
                invalidState={flightsWarning || invalidState}
                isFetchingMoreFlights={
                  returningQuery.loading ||
                  (flightsWarning && departingQuery.loading) ||
                  isCreatingRoundTrip
                }
                setSelectedTabDate={setReturningTabs}
                setReturningTabsWithoutClearingWarning={
                  setReturningTabsWithoutClearingWarning
                }
                lastAvailableDates={lastAvailableDates}
                earliestAvailableDate={earliestAvailableDates[RETURNING]}
                automationHook={withValue(FLIGHTS_LIST_VIEW.hook, RETURNING)}
                shouldAutoSelect={shouldAutoSelect[RETURNING]}
                setShouldAutoSelect={setShouldAutoSelect}
                onArrowClick={handleArrowClick}
                shouldReturnFlightOptionBeNull={shouldReturnFlightOptionBeNull}
                setShouldReturnFlightOptionBeNull={
                  setShouldReturnFlightOptionBeNull
                }
                loyaltyPointsValue={loyaltyPointsValue}
                isRedemptionEnabledForNavitaire={
                  isRedemptionEnabledForNavitaire
                }
                googleAnalyticsData={googleAnalyticsData}
                selectedBundles={selectedBundles}
                onBundleSelect={onBundleSelect}
                bundleOffers={bundleOffers}
                allAncillariesList={allAncillariesList}
              />
            )}
            <VerticalSpacing size={mq.lt('sm') ? 4 : 6} />
          </>
        ) : null}
      </>
    );
  }
);

SearchResults.propTypes = propTypes;
SearchResults.displayName = 'SearchResults';

export default SearchResults;
